import React from 'react';
import { AlertTriangle, AlertCircle, Info, HelpCircle, Loader2 } from 'lucide-react';

// Different modal types with corresponding icons and colors
export type ModalType = 'warning' | 'danger' | 'info' | 'question';

interface ModalTypeConfig {
  icon: React.ReactNode;
  color: string;
  confirmButtonClass: string;
}

const modalTypeConfigs: Record<ModalType, ModalTypeConfig> = {
  warning: {
    icon: <AlertTriangle size={24} />,
    color: 'var(--primary-color)',
    confirmButtonClass: 'button-primary'
  },
  danger: {
    icon: <AlertCircle size={24} />,
    color: 'var(--danger)',
    confirmButtonClass: 'button-danger'
  },
  info: {
    icon: <Info size={24} />,
    color: 'var(--primary-color)',
    confirmButtonClass: 'button-primary'
  },
  question: {
    icon: <HelpCircle size={24} />,
    color: 'var(--primary-color)',
    confirmButtonClass: 'button-primary'
  }
};

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  warningMessage?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: ModalType;
  confirmIcon?: React.ReactNode;
  hideCancel?: boolean;
  children?: React.ReactNode;
  isLoading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  warningMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  type = 'warning',
  confirmIcon,
  hideCancel = false,
  children,
  isLoading = false
}) => {
  if (!isOpen) return null;
  
  const typeConfig = modalTypeConfigs[type];
  
  return (
    <><div className="modal-overlay" onClick={onClose}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                  {React.cloneElement(typeConfig.icon as React.ReactElement, { color: typeConfig.color })}
                  <div className="modal-title">{title}</div>
              </div>

              <p>{message}</p>

              {warningMessage && (
                  <p className="modal-warning">{warningMessage}</p>
              )}

              {children}

              <div className="modal-actions">
                  {!hideCancel && (
                      <button className="button-subtle" onClick={onClose} disabled={isLoading}>
                          {cancelLabel}
                      </button>
                  )}

                  <button 
                    className={`${typeConfig.confirmButtonClass} ${isLoading ? 'button-loading' : ''}`} 
                    onClick={onConfirm}
                    disabled={isLoading}
                  >
                      {isLoading ? (
                        <Loader2 size={16} className="spinner" />
                      ) : confirmIcon}
                      {confirmLabel}
                  </button>
              </div>
          </div>
      </div>
      <style>{`
        /* Modal Components */
        .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        }

        .modal-content {
        width: 100%;
        max-width: 400px;
        margin: 0 auto;
        padding: 1.5rem;
        background-color: var(--bg-light);
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        box-shadow: var(--shadow-md);
        }

        .modal-header {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        gap: 0.5rem;
        }

        .modal-header-text {
        font-size: 1.25rem;
        font-weight: 600;
        }

        .modal-warning {
        color: var(--danger);
        font-weight: 500;
        margin-top: 0.5rem;
        }

        .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 1.5rem;
        }
         
        .modal-title {
            font-size: 1.2rem;
            font-weight: 600;
        }

        .button-loading {
            opacity: 0.8;
            cursor: not-allowed;
        }

        .spinner {
            animation: spin 1s linear infinite;
            margin-right: 0.375rem;
        }

        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }

        button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    `}</style>
    </>
  );
};

export default ConfirmationModal; 